import { Component, Inject, Input } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { PermissionsService } from '../../../common/services/permissions.service';
import { Member } from '../../members.models';
import { NotifyService } from '../../../common/services/notify.service';
import { ConfirmationDialogService } from '../../../common/services/confirmation-dialog.service';
import { AppState } from '../../../store';
import { selectUserId } from '../../../account/store/account.selectors';
import { ItemType } from '../../../common/components/lists/list-item-snippet.component';
import { removeMember, updateMember } from '../../store/members/members.actions';

@Component({
  selector: 'member-list-item',
  template: `
    <div class="generic-list-item slider-animation" [ngClass]="{ 'closed-animation': item.isRemoved }">
      <div class="member-list-item">
        <div class="member-img">
          <img class="img-circle" [src]="item.avatar_url" [alt]="item.name" />
        </div>
        <div class="member-body">
          <div class="member-list-details-container">
            <div class="list-member-name">
              <a
                (click)="goToMember(item)"
                [attr.disabled]="hasPermission('app.account.settings.account.member') | async"
                ><strong class="list-member-name">{{ item.name || item.email }}</strong></a
              >
              <span class="text-muted" *ngIf="item.id === userId">{{
                'member.generic-list.item.current' | translate
              }}</span>
            </div>
            <p class="list-member-email">{{ item.email }}</p>
          </div>
          <role-picker
            *ngIf="!item.owner && (hasPermission('updateMemberRole') | async)"
            class="role-picker"
            [value]="item.role"
            (onChange)="roleUpdate($event, item)"
          ></role-picker>
        </div>
        <div *ngxPermissionsOnly="'updateMemberRole'">
          <svg
            *ngIf="item.id !== userId && !item.owner"
            class="icon icons-master-expanded_icon-list-delete"
            (click)="onRemove(item)"
            [matTooltip]="'member.generic-list.item.actions.delete' | translate"
            matTooltipPosition="above"
            matTooltipClass="above"
          >
            <use href="#img-sprite_icons-master-expanded_icon-list-delete"></use>
          </svg>
        </div>
        <div *ngxPermissionsOnly="'deleteMember'">
          <svg
            *ngIf="item.id == userId && !item.owner"
            class="icon icons-master-expanded_icon-list-delete"
            (click)="onRemove(item)"
            [matTooltip]="'member.generic-list.item.actions.leave' | translate"
            matTooltipPosition="above"
            matTooltipClass="above"
          >
            <use href="#img-sprite_icons-master-expanded_icon-list-delete"></use>
          </svg>
        </div>
      </div>
    </div>
  `,
})
export class MemberListItemComponent {
  @Input() item: Member;
  userId: number;

  constructor(
    private permissionsService: PermissionsService,
    private translate: TranslateService,
    private notify: NotifyService,
    private confirmationDialog: ConfirmationDialogService,
    private store: Store<AppState>,
    @Inject('$rootScope') private $rootScope: any,
  ) {
    this.store.pipe(selectUserId).subscribe((userId: number) => {
      this.userId = userId;
    });
  }

  hasPermission(permissionName) {
    return this.permissionsService.hasPermission(permissionName);
  }

  roleUpdate(role: string, member: Member): void {
    this.store.dispatch(updateMember({ member: { ...member, role } }));
  }

  goToMember(item: Member): void {
    this.$rootScope.stateGo('app.account.settings.account.member', item);
  }

  onRemove(item: Member): void {
    if (item.id === this.userId) {
      const dialogRef = this.confirmationDialog.openDialog({
        title: this.translate.instant('member.generic-list.item.modals.leave.confirm.title'),
        hint: this.translate.instant('member.generic-list.item.modals.leave.confirm.hint'),
        yes: this.translate.instant('member.generic-list.item.modals.leave.confirm.yes'),
        no: this.translate.instant('member.generic-list.item.modals.leave.confirm.no'),
        item,
        itemType: ItemType.member,
      });
      dialogRef.afterClosed().subscribe((isConfirmed) => {
        if (isConfirmed) {
          this.store.dispatch(removeMember({ memberId: item.id, isLogout: true }));
        }
      });
    } else {
      const dialogRef = this.confirmationDialog.openDialog({
        title: this.translate.instant('member.generic-list.item.modals.delete.confirm.title'),
        hint: this.translate.instant('member.generic-list.item.modals.delete.confirm.hint'),
        yes: this.translate.instant('member.generic-list.item.modals.delete.confirm.yes'),
        no: this.translate.instant('member.generic-list.item.modals.delete.confirm.no'),
        item,
        itemType: ItemType.member,
      });

      dialogRef.afterClosed().subscribe((isConfirmed) => {
        if (isConfirmed) {
          this.store.dispatch(removeMember({ memberId: item.id, isLogout: false }));
        }
      });
    }
  }
}
